import React, { useState, useMemo, useEffect, useRef } from "react";
import Header from "../../Components/Header";
import CourseCard from "../../Components/Courses/CourseCard";
import Filter from "../../Components/Courses/Filter";
import Searchbar from "../../Components/Searchbar";
import { ChevronRight, ArrowUpDown, ListFilter } from "lucide-react";
import { initialState } from "../../Data/courseFilterData";
import { useCourses } from "../../context/CourseContext";
import { useUniversities } from "../../context/UniversityContext";
import { useLocation } from "react-router-dom";
import { db } from "../../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";

type SortField = "duration" | "institution" | "courseFee" | null;
type SortDirection = "asc" | "desc";

export default function Courses() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterState, setFilterState] = useState(initialState);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [totalCoursesCount, setTotalFirestoreCount] = useState<number | null>(
    null,
  );

  // ---------------------------------------------------------
  // ✅ Firestore Fetching Logic (Search, Filter, Sort)
  // ---------------------------------------------------------
  const fetchFromFirestore = async (isInitial: boolean) => {
    if (isFetchingMoreRef.current) return;
    if (!isInitial && localCourses.length === 0) return;

    isFetchingMoreRef.current = true;
    if (isInitial) {
      setIsInitialLoading(true);
    }

    try {
      const constraints: any[] = [];

      // Apply Firestore-compatible filters
      if (filterState.courseInfo.selectedLevel) {
        constraints.push(where("level", "==", filterState.courseInfo.selectedLevel));
      }
      if (filterState.location.selectedCountry) {
        constraints.push(where("country", "==", filterState.location.selectedCountry));
      }
      if (filterState.financials.feeBudget > 0) {
        constraints.push(where("tuitionFee", "<=", filterState.financials.feeBudget));
      }

      const q =
        constraints.length > 0
          ? query(collection(db, "course-listing"), ...constraints)
          : query(collection(db, "course-listing"));

      const snapshot = await getDocs(q);
      let fetchedDocs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort client-side
      fetchedDocs.sort((a, b) =>
        (a.courseName || "").localeCompare(b.courseName || "")
      );

      // Client-side search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        fetchedDocs = fetchedDocs.filter(
          (c) =>
            c.courseName?.toLowerCase().includes(searchLower) ||
            c.universityName?.toLowerCase().includes(searchLower)
        );
      }

      // Client-side field and major filters
      if (filterState.courseInfo.selectedFieldOfStudy) {
        fetchedDocs = fetchedDocs.filter(
          (c) => c.fieldOfStudySlug === filterState.courseInfo.selectedFieldOfStudy
        );
      }
      if (filterState.courseInfo.selectedMajor) {
        fetchedDocs = fetchedDocs.filter(
          (c) => c.majorSlug === filterState.courseInfo.selectedMajor
        );
      }
      if (filterState.location.selectedState) {
        fetchedDocs = fetchedDocs.filter(
          (c) => c.state && c.state.includes(filterState.location.selectedState)
        );
      }
      if (filterState.location.selectedInstitution) {
        fetchedDocs = fetchedDocs.filter(
          (c) => c.uniSlug === filterState.location.selectedInstitution
        );
      }
      if (filterState.financials.fullTimeDuration > 0) {
        fetchedDocs = fetchedDocs.filter(
          (c) => c.duration <= filterState.financials.fullTimeDuration
        );
      }

      if (isInitial) {
        setLocalCourses(fetchedDocs);
      } else {
        setLocalCourses((prev) => [...prev, ...fetchedDocs]);
      }

      setTotalFirestoreCount(fetchedDocs.length);
      setHasMoreFirebase(false);
    } catch (error) {
      console.error("Error fetching from Firestore:", error);
    } finally {
      isFetchingMoreRef.current = false;
      setIsInitialLoading(false);
    }
  };

  // Trigger initial fetch and re-fetch on sort or filter change
  useEffect(() => {
    fetchFromFirestore(true);
  }, [searchTerm, sortField, sortDirection, filterState]);

  const { universities } = useUniversities();
  const [localCourses, setLocalCourses] = useState<any[]>([]);
  const [hasMoreFirebase, setHasMoreFirebase] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const isFetchingMoreRef = useRef(false);

  // Mapping UI sort fields to Typesense/Firestore document keys
  const sortFieldMap: Record<string, string> = {
    duration: "duration",
    institution: "universityName",
    courseFee: "tuitionFee",
  };

  const {
    metadata,
    unfilteredCourses,
    setUnfilteredCourses,
    unfilteredTotalCount,
    setUnfilteredTotalCount,
  } = useCourses();

  // ✅ Build university logo map once
  const uniLogoMap = useMemo(() => {
    const map = new Map<string, string | null>();
    universities.forEach((uni) => {
      let localLogoUrl: string | null = null;
      if (uni.logo) {
        try {
          localLogoUrl = new URL(
            `../../assets/images/${uni.logo}`,
            import.meta.url,
          ).href;
        } catch (err) {
          console.warn("Invalid logo file:", uni.logo);
        }
      }
      map.set(uni.universityName.toLowerCase().trim(), localLogoUrl);
    });
    return map;
  }, [universities]);

  // ✅ Attach logos to courses
  const courseListingData = useMemo(() => {
    return localCourses.map((course) => ({
      ...course,
      uniImg:
        uniLogoMap.get(course.universityName?.toLowerCase().trim()) ||
        new URL("../../assets/images/placeholder.png", import.meta.url).href,
    }));
  }, [localCourses, uniLogoMap]);

  const location = useLocation();

  // when defining your states
  const [selectedFieldOfStudy, setSelectedFieldOfStudy] = useState("");

  // detect incoming filter state from navigation
  useEffect(() => {
    if (location.state?.preselectedFieldOfStudy) {
      const field = location.state.preselectedFieldOfStudy;
      setSelectedFieldOfStudy(field);
      setFilterState((prev) => ({
        ...prev,
        courseInfo: {
          ...prev.courseInfo,
          selectedFieldOfStudy: field,
        },
      }));

      // Prevent reapplying after user clears filters
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Infinite scroll listener
  useEffect(() => {
    const handleScroll = async () => {
      if (
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 500
      ) {
        // Trigger Typesense next page fetch
        if (hasMoreFirebase && !isFetchingMoreRef.current) {
          fetchFromTypesense(false);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMoreFirebase, sortField, sortDirection]);

  // ✅ Handlers
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ChevronRight className="rotate-90" />;
    }
    return sortDirection === "asc" ? (
      <ChevronRight className="rotate-90" />
    ) : (
      <ChevronRight className="-rotate-90" />
    );
  };

  return (
    <div>
      <Header />
      <div className="flex flex-col items-center justify-center mx-auto lg:gap-5 max-w-[1024px] w-full mt-5 lg:mt-10 px-6">
        {/* <h1 className="hidden md:flex">
          Am I entitled to apply for a scholarship?
        </h1> */}

        {/* Search Bar */}
        <div className="flex w-full sticky top-20 items-center justify-center bg-white py-2">
          <div className="w-full mx-4 md:w-1/2">
            <Searchbar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search Courses or Universities"
            />
          </div>
        </div>

        {/* Desktop Filters */}
        <div className="hidden lg:block w-full mt-10">
          <Filter
            onFilterChange={setFilterState}
            preselectedFieldOfStudy={selectedFieldOfStudy}
          />
        </div>

        {/* Sorting + Mobile Controls */}
        <div className="flex w-full justify-between sticky top-32 py-2 bg-white shadow lg:shadow-none">
          <div className="hidden lg:flex gap-2">
            <ArrowUpDown />
            <button
              onClick={() => handleSort("duration")}
              className="flex items-center gap-1"
            >
              <p>Duration</p>
              {getSortIcon("duration")}
            </button>
            <button
              onClick={() => handleSort("institution")}
              className="flex items-center gap-1"
            >
              <p>Institutions</p>
              {getSortIcon("institution")}
            </button>
            <button
              onClick={() => handleSort("courseFee")}
              className="flex items-center gap-1"
            >
              <p>Course Fee</p>
              {getSortIcon("courseFee")}
            </button>
          </div>
          <div className="hidden lg:flex">
            {localCourses.length} of{" "}
            {totalCoursesCount !== null ? totalCoursesCount : "..."} Courses
            Shown!
          </div>

          {/* Mobile view sort & filter */}
          <div className="flex lg:hidden w-full relative">
            <div className="flex flex-row justify-center gap-20 mx-6 w-full">
              <button
                onClick={() => setIsSortOpen((v) => !v)}
                className="flex gap-2 relative"
              >
                <ArrowUpDown /> Sort
              </button>
              <button
                onClick={() => setIsFilterOpen(true)}
                className="flex gap-2"
              >
                <ListFilter /> Filter
              </button>
            </div>

            {/* Sort Overlay */}
            <div
              className={`absolute top-full left-0 w-full bg-black/50 z-10 transition-opacity duration-300 ${
                isSortOpen
                  ? "opacity-100 h-screen"
                  : "opacity-0 h-0 pointer-events-none"
              }`}
              onClick={() => setIsSortOpen(false)}
            />

            <div
              className={`bg-white left-0 w-full shadow flex flex-col justify-center z-50 px-6 py-2 top-full absolute transition-all duration-300 transform gap-2 ${
                isSortOpen
                  ? "translate-y-0 opacity-100"
                  : "-translate-y-4 opacity-0 pointer-events-none"
              }`}
              style={{ transitionTimingFunction: "ease-in-out" }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => handleSort("duration")}
                className="flex items-center gap-1"
              >
                <p>Duration</p>
                {getSortIcon("duration")}
              </button>
              <button
                onClick={() => handleSort("institution")}
                className="flex items-center gap-1"
              >
                <p>Institutions</p>
                {getSortIcon("institution")}
              </button>
              <button
                onClick={() => handleSort("courseFee")}
                className="flex items-center gap-1"
              >
                <p>Course Fee</p>
                {getSortIcon("courseFee")}
              </button>
            </div>
          </div>
        </div>

        {/* Course List */}
        <div className="flex lg:w-[1024px] pt-2 md:px-6">
          <div className="flex flex-col gap-8 lg:mx-0 lg:w-3/4">
            {courseListingData.length > 0 ? (
              courseListingData.map((course, index) => (
                <CourseCard key={index} {...course} uniImg={course.uniImg} />
              ))
            ) : (
              <div className="flex items-center justify-center w-full py-8 text-gray-500 text-lg">
                No course found
              </div>
            )}
          </div>
        </div>

        {/* Mobile Filter Overlay */}
        <div
          className={`fixed inset-x-0 top-20 bottom-0 z-10 lg:hidden transition-transform duration-300 ease-in-out ${
            isFilterOpen ? "translate-x-0" : "translate-x-full"
          }`}
          aria-hidden={!isFilterOpen}
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsFilterOpen(false)}
          />
          <div className="relative ml-auto h-full w-full bg-white overflow-y-auto">
            <Filter
              onFilterChange={setFilterState}
              onClose={() => setIsFilterOpen(false)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
